import React, { useState, useEffect, useRef } from "react"
import { Box, Typography, Collapse } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import JumpLink from "../Link/JumpLink"
import { TableOfContentsItem } from "../../utils/tableOfContents"
import useSiteMetadata from "../../hooks/useSiteMetadata"

interface Props {
  items: TableOfContentsItem[]
  className?: string
}

const TableOfContents: React.FC<Props> = ({ items, className }) => {
  const { classes, cx } = useStyles()
  const [mobileExpanded, setMobileExpanded] = useState(false)
  const barRef = useRef<HTMLDivElement | null>(null)

  const siteMetadata = useSiteMetadata()

  const getTableOfContentsText = (): string => {
    const brandIdentifier: string = siteMetadata.brandName || siteMetadata.domainName || ""

    const normalizedBrand = brandIdentifier.toLowerCase()

    switch (normalizedBrand) {
      case "topcv.de":
        return "Inhaltsverzeichnis"
      case "topcv.fr":
        return "Table des matières"
      default:
        return "Table of contents"
    }
  }

  const tableOfContentsText = getTableOfContentsText()

  useEffect(() => {
    if (typeof window === "undefined") return

    let ticking = false

    const updateProgress = () => {
      const el = document.documentElement
      const progress = Math.min((el.scrollTop + el.clientHeight) / el.scrollHeight, 1)
      if (barRef.current) {
        barRef.current.style.transform = `scaleX(${progress})`
      }
    }

    const onScroll = () => {
      if (!ticking) {
        ticking = true
        requestAnimationFrame(() => {
          updateProgress()
          ticking = false
        })
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", updateProgress, { passive: true })
    updateProgress()

    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", updateProgress)
    }
  }, [])

  if (!items || items.length === 0) {
    return null
  }

  const renderItem = (item: TableOfContentsItem, level = 0) => {
    const cleanText = item.text

    return (
      <div key={item.id} className={classes.item}>
        <JumpLink
          hash={item.id}
          className={cx(classes.link, {
            [classes.mainLink]: level === 0,
            [classes.subLink]: level > 0,
          })}
        >
          {level > 0 && "• "}
          {cleanText}
        </JumpLink>
        {item.children.length > 0 && level < 1 && (
          <div className={classes.children}>{item.children.map((child) => renderItem(child, level + 1))}</div>
        )}
      </div>
    )
  }

  return (
    <Box className={cx(classes.root, className)}>
      <Box className={classes.mobileHeader} onClick={() => setMobileExpanded(!mobileExpanded)}>
        <Box className={classes.mobileHeaderContent}>
          <Box className={classes.mobileIcon}>
            <svg width="18" height="12" viewBox="0 0 18 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 12H18V10H0V12ZM0 7H18V5H0V7ZM0 0V2H18V0H0Z" fill="#007bff" />
            </svg>
          </Box>
          <Typography className={classes.title}>{tableOfContentsText}</Typography>
        </Box>
      </Box>

      <div className={classes.desktopHeader}>
        <Typography className={classes.desktopTitle}>{tableOfContentsText}</Typography>
        <div className={classes.progressContainer}>
          <div className={classes.progressTrack}>
            <div ref={barRef} className={classes.progressBar} />
          </div>
        </div>
      </div>

      <Collapse in={mobileExpanded} className={classes.mobileContent}>
        <div>{items.map((item) => renderItem(item, 0))}</div>
      </Collapse>

      <div className={classes.desktopContent}>{items.map((item) => renderItem(item, 0))}</div>
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  root: {
    paddingTop: "32px",
    [theme.breakpoints.down("md")]: {
      padding: "16px",
      marginBottom: "24px",
    },
  },

  mobileHeader: {
    display: "none",
    alignItems: "center",
    justifyContent: "flex-start",
    cursor: "pointer",
    marginBottom: "12px",
    padding: "12px 16px",
    [theme.breakpoints.down("md")]: {
      display: "flex",
    },
  },

  mobileHeaderContent: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  mobileIcon: {
    width: "18px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  title: {
    fontSize: "16px",
    fontWeight: 400,
    color: "#212529",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    margin: 0,
  },

  desktopHeader: {
    display: "block",
    marginBottom: "16px",
    position: "sticky",
    top: "0px",
    backgroundColor: "white",
    padding: "0px 0px 0px 12px",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },

  desktopTitle: {
    fontSize: "16px",
    fontWeight: 400,
    color: "#212529",
    marginBottom: "8px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },

  progressContainer: {
    marginBottom: "8px",
  },

  progressTrack: {
    width: "100%",
    height: "2px",
    backgroundColor: "#e9ecef",
    borderRadius: "1px",
    overflow: "hidden",
  },

  progressBar: {
    height: "100%",
    backgroundColor: "#6c757d",
    borderRadius: "1px",
    transformOrigin: "left center",
    transform: "scaleX(0)",
    transition: "transform 120ms linear",
    willChange: "transform",
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
  },

  mobileContent: {
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },

  desktopContent: {
    display: "block",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },

  item: {
    marginBottom: "8px",
    borderRadius: "8px",
    transition: "background-color 0.2s ease",
    padding: "4px 12px",
    margin: "2px 0",
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
  },

  children: {
    paddingLeft: "8px",
    "& $item": {
      marginBottom: "6px",
    },
    [theme.breakpoints.down("md")]: {
      paddingLeft: "16px",
    },
  },

  link: {
    display: "inline-block",
    textDecoration: "none !important",
    color: "#6c757d",
    fontSize: "14px",
    lineHeight: "1.5",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#495057 !important",
      textDecoration: "none !important",
    },
    "&:focus": {
      outline: "none",
      color: "#495057 !important",
    },
  },

  mainLink: {
    fontWeight: 400,
    fontSize: "18px !important",
  },

  subLink: {
    fontWeight: 400,
    color: "#868e96",
    paddingTop: "8px",
  },
}))

export default TableOfContents
